import { useEffect, useMemo } from 'react';

import { EventCurrencySwap } from '../event-currency-swap';
import { EventTotalCostTooltip } from '../event-total-cost-tooltip';
import { EventDiscount } from './event-discount';

import { UsdcIcon } from '@/components/common/icons/usdc';
import { Skeleton } from '@/components/ui/skeleton';
import { useFetchGlobalState } from '@/hooks/solana/query/use-fetch-global-state';
import { useFetchMarketState } from '@/hooks/solana/query/use-fetch-market-state';
import { useFetchApproxExchangeRate } from '@/hooks/solana/query/use-fetch-quote';
import { useFetchUserState } from '@/hooks/solana/query/use-fetch-user-state';
import { useAuthenticatedWallet } from '@/hooks/ui/use-authenticated-wallet';
import { getLiquidityIncentiveFeeDiscount } from '@/lib/utils';
import { useOrderbookStore } from '@/store/orderbook';

type Props = {
  marketId: number;
  receiveAmountWithoutFee: string | number;
  price: string | number | null; // NULL FOR MARKET ORDERS
  matchingAmount: number;
  nextBestPrice: number | null;
  solAmount: number | string;
  tradeType: 'buy' | 'sell';
  avgPrice: number | string;
  includeCurrencySwap?: boolean;
};

export const EventBuyReceive = ({
  marketId,
  receiveAmountWithoutFee,
  price,
  matchingAmount,
  nextBestPrice,
  solAmount,
  tradeType,
  avgPrice,
  includeCurrencySwap = true,
}: Props) => {
  const { isUsdc, setSolAmount } = useOrderbookStore();
  const { publicKey } = useAuthenticatedWallet();

  const { data: marketState } = useFetchMarketState(marketId);
  const { data: globalState } = useFetchGlobalState();
  const { data: userState } = useFetchUserState(publicKey);
  const { isLoading: isApproxExchangeRateLoading } =
    useFetchApproxExchangeRate();

  const fee = useMemo(() => {
    if (!globalState)
      return {
        marketFee: 0,
        limitFee: 0,
        liquidityIncentiveFeeDiscount: 0,
        spread: 0,
      };

    const tradeFee = Number(marketState?.tradeFeePercent ?? 0) / 10000;
    const referralFee = userState?.referrer
      ? Number(globalState.referralFeeDiscount) / 10000
      : 0;

    const discount =
      Number(globalState.liquidityIncentiveDiscountPerSpread) / 10000;
    const maxSpread = Number(globalState.liquidityIncentiveMaxSpread);

    return getLiquidityIncentiveFeeDiscount(
      tradeFee,
      referralFee,
      discount,
      maxSpread,
      price,
      nextBestPrice,
    );
  }, [
    globalState,
    marketState?.tradeFeePercent,
    nextBestPrice,
    price,
    userState?.referrer,
  ]);

  const cost = useMemo(() => {
    const marketAmount = Number(matchingAmount) || 0;
    const totalUsd = Number(receiveAmountWithoutFee) || 0;
    const limitAmount = Math.max(0, totalUsd - marketAmount);
    const limitPercent = totalUsd > 0 ? limitAmount / totalUsd : 0;

    const totalSol = Number(solAmount) || 0;
    const limitAmountSol = totalSol * limitPercent;
    const marketAmountSol = totalSol - limitAmountSol;

    const marketFee = (isUsdc ? marketAmount : marketAmountSol) * fee.marketFee;
    const limitFee = (isUsdc ? limitAmount : limitAmountSol) * fee.limitFee;

    const amountWithFee = isUsdc
      ? marketAmount + marketFee + limitAmount + limitFee
      : marketAmountSol + marketFee + limitAmountSol + limitFee;

    return {
      amount: isUsdc ? totalUsd : totalSol,
      amountWithFee,
      feeAmount: marketFee + limitFee,
    };
  }, [
    isUsdc,
    matchingAmount,
    receiveAmountWithoutFee,
    solAmount,
    fee.marketFee,
    fee.limitFee,
  ]);

  useEffect(() => {
    if (!includeCurrencySwap) return;
    setSolAmount(isUsdc ? 0 : cost.amountWithFee);
  }, [includeCurrencySwap, isUsdc, cost.amountWithFee, setSolAmount]);

  return (
    <div className="flex flex-row text-[20px] font-medium sm:text-[24px]">
      <div className="flex grow items-center">
        <p className="relative">Total cost</p>
        <EventDiscount
          marketId={marketId}
          nextBestPrice={nextBestPrice}
          isMarketLimit={
            matchingAmount > 0 && +receiveAmountWithoutFee > matchingAmount
          }
          discount={
            +receiveAmountWithoutFee - matchingAmount > 0
              ? fee.liquidityIncentiveFeeDiscount
              : 0
          }
          spread={fee.spread}
          tradeType={tradeType}
        />
      </div>
      <EventTotalCostTooltip cost={{ ...cost, avgPrice }}>
        <div className="flex flex-row items-center justify-center gap-[10px] text-[20px] sm:text-[24px]">
          {isApproxExchangeRateLoading && !isUsdc ? (
            <Skeleton className="h-[32px] w-[100px]" />
          ) : (
            `${cost.amountWithFee.toFixed(isUsdc ? 2 : 4)}`
          )}
          {includeCurrencySwap ? (
            <EventCurrencySwap />
          ) : (
            <UsdcIcon className="size-6 sm:size-8" />
          )}
        </div>
      </EventTotalCostTooltip>
    </div>
  );
};
